import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Blog.css';

const emptyForm = {
    slug: '',
    title: '',
    body: '',
    visibility: 'public',
    published: false,
};

function slugify(title) {
    return title
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
}

function formatDate(iso) {
    if (!iso) return 'Draft';
    return new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
}

const AdminBlog = () => {
    const navigate = useNavigate();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [slugTouched, setSlugTouched] = useState(false);
    const fileInputRef = React.useRef(null);

    const fetchPosts = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const r = await fetch('/api/admin/blog', { credentials: 'include' });
            if (!r.ok) throw new Error('Failed to load posts');
            setPosts(await r.json());
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchPosts(); }, [fetchPosts]);

    const resetForm = () => {
        setEditingId(null);
        setForm(emptyForm);
        setSlugTouched(false);
    };

    const startNew = () => {
        resetForm();
    };

    const startEdit = async (id) => {
        setError('');
        try {
            const r = await fetch(`/api/admin/blog/${id}`, { credentials: 'include' });
            if (!r.ok) throw new Error('Failed to load post');
            const post = await r.json();
            setEditingId(post.id);
            setForm({
                slug: post.slug,
                title: post.title,
                body: post.body,
                visibility: post.visibility,
                published: Boolean(post.published_at),
            });
            setSlugTouched(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (err) {
            setError(err.message);
        }
    };

    const handleTitleChange = (title) => {
        setForm(prev => ({
            ...prev,
            title,
            slug: slugTouched ? prev.slug : slugify(title),
        }));
    };

    const savePost = async (publishOverride) => {
        setError('');
        setSaving(true);
        const payload = {
            ...form,
            published: publishOverride !== undefined ? publishOverride : form.published,
        };
        try {
            const url = editingId ? `/api/admin/blog/${editingId}` : '/api/admin/blog';
            const method = editingId ? 'PATCH' : 'POST';
            const r = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(payload),
            });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(data.error || 'Save failed');
            resetForm();
            await fetchPosts();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const uploadImage = async (file) => {
        if (!file) return;
        setError('');
        setUploading(true);
        try {
            const data = new FormData();
            data.append('file', file);
            data.append('visibility', form.visibility);
            const alt = file.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ');
            data.append('alt', alt);

            const r = await fetch('/api/admin/blog/media', {
                method: 'POST',
                credentials: 'include',
                body: data,
            });
            const json = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(json.error || 'Upload failed');

            const markdown = `\n![${json.alt_text || alt}](${json.url})\n`;
            setForm(prev => ({ ...prev, body: prev.body + markdown }));
        } catch (err) {
            setError(err.message);
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const deletePost = async (id, title) => {
        if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
        setError('');
        try {
            const r = await fetch(`/api/admin/blog/${id}`, {
                method: 'DELETE',
                credentials: 'include',
            });
            if (!r.ok) {
                const data = await r.json().catch(() => ({}));
                throw new Error(data.error || 'Delete failed');
            }
            if (editingId === id) resetForm();
            await fetchPosts();
        } catch (err) {
            setError(err.message);
        }
    };

    return (
        <div className="admin-blog">
            <h1>Blog posts</h1>
            <p className="admin-blog-intro">
                Write markdown posts. Set visibility to <strong>public</strong> or{' '}
                <strong>members</strong> (any approved logged-in user). Upload images below —
                they inherit the post visibility setting.
            </p>

            <div className="admin-blog-toolbar">
                <button type="button" className="admin-blog-btn admin-blog-btn-secondary" onClick={() => navigate('/admin')}>
                    Back to admin
                </button>
                <button type="button" className="admin-blog-btn" onClick={startNew}>
                    New post
                </button>
            </div>

            {error && <div className="alert-error">{error}</div>}

            <form
                className="admin-blog-form"
                onSubmit={e => {
                    e.preventDefault();
                    savePost();
                }}
            >
                <h2>{editingId ? 'Edit post' : 'New post'}</h2>

                <label>
                    Title
                    <input
                        type="text"
                        value={form.title}
                        onChange={e => handleTitleChange(e.target.value)}
                        required
                        disabled={saving}
                    />
                </label>

                <label>
                    Slug (URL path)
                    <input
                        type="text"
                        value={form.slug}
                        onChange={e => {
                            setSlugTouched(true);
                            setForm(prev => ({ ...prev, slug: e.target.value }));
                        }}
                        required
                        pattern="[a-z0-9]+(-[a-z0-9]+)*"
                        title="Lowercase letters, numbers, and hyphens only"
                        disabled={saving}
                    />
                </label>

                <label>
                    Visibility
                    <select
                        value={form.visibility}
                        onChange={e => setForm(prev => ({ ...prev, visibility: e.target.value }))}
                        disabled={saving}
                    >
                        <option value="public">Public — anyone can read</option>
                        <option value="members">Members — logged-in users only</option>
                    </select>
                </label>

                <label>
                    Body (Markdown)
                    <textarea
                        value={form.body}
                        onChange={e => setForm(prev => ({ ...prev, body: e.target.value }))}
                        disabled={saving || uploading}
                    />
                </label>

                <div className="admin-blog-upload">
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        className="admin-blog-file-input"
                        disabled={saving || uploading}
                        onChange={e => uploadImage(e.target.files?.[0])}
                    />
                    <button
                        type="button"
                        className="admin-blog-btn admin-blog-btn-secondary"
                        disabled={saving || uploading}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        {uploading ? 'Uploading…' : 'Upload image'}
                    </button>
                    <span className="admin-blog-upload-hint">JPEG, PNG, GIF, or WebP · max 10 MB</span>
                </div>

                <label>
                    <input
                        type="checkbox"
                        checked={form.published}
                        onChange={e => setForm(prev => ({ ...prev, published: e.target.checked }))}
                        disabled={saving}
                    />
                    {' '}Published
                </label>

                <div className="admin-blog-form-actions">
                    <button type="submit" className="admin-blog-btn" disabled={saving}>
                        {saving ? 'Saving…' : 'Save'}
                    </button>
                    <button
                        type="button"
                        className="admin-blog-btn"
                        disabled={saving}
                        onClick={() => savePost(true)}
                    >
                        {saving ? 'Saving…' : 'Save & publish'}
                    </button>
                    {editingId && form.published && (
                        <Link to={`/blog/${form.slug}`} className="admin-blog-btn admin-blog-btn-secondary">
                            View live
                        </Link>
                    )}
                    {editingId && (
                        <button
                            type="button"
                            className="admin-blog-btn admin-blog-btn-secondary"
                            onClick={resetForm}
                            disabled={saving}
                        >
                            Cancel edit
                        </button>
                    )}
                </div>
            </form>

            {loading ? (
                <p>Loading posts…</p>
            ) : (
                <table className="admin-blog-posts-table">
                    <thead>
                        <tr>
                            <th>Title</th>
                            <th>Slug</th>
                            <th>Visibility</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {posts.map(post => (
                            <tr key={post.id}>
                                <td>{post.title}</td>
                                <td><code>{post.slug}</code></td>
                                <td>{post.visibility}</td>
                                <td>
                                    {post.published_at ? (
                                        formatDate(post.published_at)
                                    ) : (
                                        <span className="blog-badge blog-badge-draft">Draft</span>
                                    )}
                                </td>
                                <td>
                                    <div className="admin-blog-row-actions">
                                        <button type="button" onClick={() => startEdit(post.id)}>Edit</button>
                                        {post.published_at && (
                                            <button type="button" onClick={() => navigate(`/blog/${post.slug}`)}>
                                                View
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => deletePost(post.id, post.title)}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {posts.length === 0 && (
                            <tr>
                                <td colSpan="5">No posts yet.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default AdminBlog;
